import { Text } from "@dropbox/dig-components/typography";
import { UIIcon } from "@dropbox/dig-icons";
import { CloseLine } from "@dropbox/dig-icons/dist/mjs/assets";
import { StacksMissingMini } from "@dropbox/dig-illustrations";
import { PAP_Click_RemoveSource } from "@mirage/analytics/events/types/click_remove_source";
import { useFeatureFlagValueBool } from "@mirage/service-experimentation/useFeatureFlagValue";
import { getSourceUUID } from "@mirage/shared/compose/compose-session";
import i18n from "@mirage/translations";
import classNames from "classnames";
import { memo } from "react";
import { ComposeSourceRow } from "./ComposeSourceRow";
import styles from "./ComposeSources.module.css";
import { SourcesSearch } from "./SourcesSearch";

import type { PAPEvent } from "@mirage/analytics/events/base/event";
import type { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import type { ChatSource } from "@mirage/mosaics/Chat/types";
import type { ReactNode } from "react";
import type { SourcesContentCache } from "../../data/ComposeSourcesCache";

interface ComposeSourcesProps {
  sources: ChatSource[];
  /* if sourcesContentCache is provided, the rows will display the loading state of the source */
  sourcesContentCache?: SourcesContentCache;
  onAddSource: (source: ChatSource) => void;
  onRemoveSource: (source: ChatSource) => void;
  sourcesTitleLabel?: ReactNode;
  searchTitleLabel?: ReactNode;
  logComposeEvent: (
    event: PAPEvent,
    overrides?: {
      actionSurfaceComponent?: ActionSurfaceComponent;
    },
  ) => void;
  hideEmptySources?: boolean;
  hasBorder?: boolean;
  hideSearchResultsLabel?: boolean;
  offsetEmptyMargin?: boolean;
  uploadButton?: ReactNode;
  emptyStateTitle?: string;
  closeResultsOnDragDrop?: boolean;
}
export const ComposeSources = memo(
  ({
    sources,
    sourcesContentCache,
    onAddSource,
    onRemoveSource,
    sourcesTitleLabel,
    searchTitleLabel,
    logComposeEvent,
    hideEmptySources,
    hasBorder,
    hideSearchResultsLabel,
    offsetEmptyMargin,
    uploadButton,
    emptyStateTitle = i18n.t("compose_sources_empty_state_title"),
    closeResultsOnDragDrop,
  }: ComposeSourcesProps) => {
    const isFeatureEnabled =
      useFeatureFlagValueBool("dash_2025_05_21_assist_add_sources_filter") ??
      false;
    const currentSourcesList =
      sources.length === 0 && hideEmptySources ? undefined : (
        <>
          {sources.length === 0 ? (
            <div
              className={classNames(styles.SourcesEmpty, {
                [styles.SourcesEmptyMargin]: offsetEmptyMargin,
              })}
            >
              <div className={styles.StacksMissingIllustration}>
                <StacksMissingMini altText="Upload stack icon" />
              </div>
              <div>
                <Text size="medium" color="subtle" tagName="div">
                  {emptyStateTitle}
                </Text>
                <Text size="xsmall" color="subtle" tagName="div">
                  {i18n.t("compose_sources_empty_state_sources_subtitle")}
                </Text>
              </div>
            </div>
          ) : (
            <div
              className={classNames(styles.SourceRows, {
                [styles.SourceRowsBordered]: hasBorder,
              })}
            >
              {sources.map((source, i) => {
                const sourceUUID = getSourceUUID(source);
                if (!sourceUUID) {
                  return null;
                }
                return (
                  <ComposeSourceRow
                    key={sourceUUID || i}
                    source={source}
                    action={{
                      icon: (
                        <UIIcon
                          src={CloseLine}
                          className={styles.SourceRowCloseIcon}
                        />
                      ),
                      variant: "small",
                      onClick: () => {
                        logComposeEvent(PAP_Click_RemoveSource());
                        onRemoveSource(source);
                      },
                      label: i18n.t("compose_source_row_remove_button_label"),
                    }}
                    loadState={
                      sourcesContentCache
                        ? sourcesContentCache[sourceUUID]?.state
                        : undefined
                    }
                  />
                );
              })}
            </div>
          )}
        </>
      );
    return (
      <>
        <div className={styles.SearchTitleRow}>{searchTitleLabel}</div>
        <div>
          {isFeatureEnabled ? (
            <SourcesSearch
              existingSources={sources}
              onAddSource={onAddSource}
              logComposeEvent={logComposeEvent}
              hasBorder={true}
              uploadButton={uploadButton}
              enableFilters={true}
              closeResultsOnDragDrop={closeResultsOnDragDrop}
            />
          ) : (
            <SourcesSearch
              existingSources={sources}
              onAddSource={onAddSource}
              hasBorder={hasBorder}
              hideSearchResultsLabel={hideSearchResultsLabel}
              logComposeEvent={logComposeEvent}
              uploadButton={uploadButton}
              closeResultsOnDragDrop={closeResultsOnDragDrop}
            />
          )}
        </div>
        <div className={styles.SourcesListContainer}>
          {sources.length > 0 && (
            <div className={styles.SourcesListTitle}>{sourcesTitleLabel}</div>
          )}
          {currentSourcesList}
        </div>
      </>
    );
  },
);
ComposeSources.displayName = "ComposeSources";

const Divider = memo(() => {
  return <div className={styles.Divider} />;
});
Divider.displayName = "Divider";
